import { RegionId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useState } from "react";
import { useHistory } from "react-router";
import { useIsMultiRegionUser } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { businessProfilePath } from "./business-profile-types";
import {
    BusinessCard,
    NotStartedCard,
    UncoveredOfficeCard,
    WhatToHaveOnHand,
} from "./business-verification-cards";
import {
    BusinessProfileOutput,
    useBusinessProfiles,
    useCoveredOfficesPhrase,
    useOfficeName,
    useRefreshBusinessProfiles,
} from "./use-business-verification";

const useOfficeActions = () => {
    const trpcClient = trpc.useTRPC();
    const history = useHistory();
    const refresh = useRefreshBusinessProfiles();
    const isMultiOffice = useIsMultiRegionUser();
    const [error, setError] = useState<string | null>(null);
    const start = useMutation(trpcClient.businessProfile.start.mutationOptions());
    const updateOffices = useMutation(
        trpcClient.businessProfile.updateOffices.mutationOptions(),
    );

    const startFor = async (regionId: RegionId) => {
        setError(null);
        try {
            const profile = await start.mutateAsync({ regionId });
            handleEvent("business-verification-start", { multiOffice: isMultiOffice });
            await refresh();
            history.push(businessProfilePath(profile.id));
        } catch {
            setError("We couldn't start verifying this business. Try again in a moment.");
        }
    };

    const join = async (profile: BusinessProfileOutput, regionId: RegionId) => {
        setError(null);
        try {
            await updateOffices.mutateAsync({
                id: profile.id,
                officeIds: [...profile.officeIds, regionId],
            });
            await refresh();
        } catch {
            setError(
                "We couldn't add this office to the business. Try again in a moment.",
            );
        }
    };

    return {
        error,
        busy: start.isPending || updateOffices.isPending,
        startFor: (regionId: RegionId) => {
            startFor(regionId).catch((): void => undefined);
        },
        join: (profile: BusinessProfileOutput, regionId: RegionId) => {
            join(profile, regionId).catch((): void => undefined);
        },
    };
};

export const BusinessVerificationPage: React.FC = () => {
    const isMultiOffice = useIsMultiRegionUser();
    const officeName = useOfficeName();
    const coveredOffices = useCoveredOfficesPhrase();
    const { profilesQuery, uncoveredOfficesQuery } = useBusinessProfiles();
    const actions = useOfficeActions();
    const profiles = profilesQuery.data ?? [];
    const uncoveredOfficeIds = uncoveredOfficesQuery.data ?? [];
    const joinable = profiles.filter(p => p.editable && p.status === "draft");
    const loading = profilesQuery.isLoading || uncoveredOfficesQuery.isLoading;

    return (
        <ContentFull>
            <div className="space-y-4">
                <ContentHeader>SMS &amp; Voice Business Verification</ContentHeader>
                <ContentSubheader>
                    Phone carriers check who is behind every business number. Verifying
                    your business keeps your calls from being marked as spam and your
                    texts from being blocked.
                </ContentSubheader>
                {actions.error ? (
                    <InlineBanner type="error">{actions.error}</InlineBanner>
                ) : null}
                {profilesQuery.isError || uncoveredOfficesQuery.isError ? (
                    <RetryableErrorBanner
                        message="We couldn't load your businesses."
                        retrying={
                            profilesQuery.isFetching || uncoveredOfficesQuery.isFetching
                        }
                        onRetry={() => {
                            profilesQuery.refetch().catch((): void => undefined);
                            uncoveredOfficesQuery.refetch().catch((): void => undefined);
                        }}
                    />
                ) : null}
                {loading ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4">
                        {profiles.map(profile => (
                            <BusinessCard
                                key={profile.id}
                                profile={profile}
                                isMultiOffice={isMultiOffice}
                                coveredOffices={coveredOffices}
                            />
                        ))}
                        {isMultiOffice &&
                        joinable.length === 0 &&
                        uncoveredOfficeIds.length > 0 ? (
                            <WhatToHaveOnHand />
                        ) : null}
                        {uncoveredOfficeIds.map(regionId =>
                            isMultiOffice ? (
                                <UncoveredOfficeCard
                                    key={regionId}
                                    officeName={officeName(regionId)}
                                    joinableBusinesses={joinable}
                                    busy={actions.busy}
                                    onJoin={profile => actions.join(profile, regionId)}
                                    onStart={() => actions.startFor(regionId)}
                                />
                            ) : (
                                <NotStartedCard
                                    key={regionId}
                                    busy={actions.busy}
                                    onStart={() => actions.startFor(regionId)}
                                />
                            ),
                        )}
                    </div>
                )}
            </div>
        </ContentFull>
    );
};
